const { some } = require('lodash');

const NOT_SELECTED_FIELD = -1;
const CREDIT_CARD_FIELD_STRUCTURE_MONTH = 0;
const CREDIT_CARD_FIELD_STRUCTURE_YEAR = 1;
const CREDIT_CARD_FIELD_STRUCTURE_CVV = 2;

class CreditCardItemEditController {
    constructor($sce, $timeout, $element, settings, digitalPayPaymentErrorDisplayService, iframeEventHandler, digitalPayFormValidator) {
        this.$sce = $sce;
        this.$timeout = $timeout;
        this.$element = $element;
        this.settings = settings;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.iframeEventHandler = iframeEventHandler;
        this.digitalPayFormValidator = digitalPayFormValidator;
    }

    $onInit() {
        this.creditCardEmptyField = NOT_SELECTED_FIELD;
        this.creditCardFieldStructureMonth = CREDIT_CARD_FIELD_STRUCTURE_MONTH;
        this.creditCardFieldStructureYear = CREDIT_CARD_FIELD_STRUCTURE_YEAR;
        this.creditCardFieldStructureCvv = CREDIT_CARD_FIELD_STRUCTURE_CVV;

        this.uniqueId = `stepUp-${this.card.PaymentInstrumentId}`;
        this.iframeUrl = undefined;
        this.iframeLoadTimeoutPromise = undefined;
        this.timeoutPeriodInMillseconds = this.settings.digitalPay.iframeTimeoutInMilliseconds;
        this.isLoading = true;
        this.isIframeLoadedSuccess = false;
        this.isUpdating = false;
        this.isValid = false;
        this.updateText = this.updateText || 'Update';
        this.errorMessage = this.settings.digitalPayError.creditCardEditCaptureFormLoadFailure;
        this.fieldStructure = [undefined, undefined, undefined]; // Setup initial field structure for each of the iframe fields
        this.currentSelectedField = this.creditCardEmptyField;
        this.setupIframe();
        this.setupListenerCallbacks();
        this.registerEventListeners();
    }

    $onDestroy() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.disposeEventListeners();
        this.clearValidationState();
    }

    updateCard() {
        if (!this.isValid) {
            return;
        }

        this.isUpdating = true;
        this.iframeEventHandler.emit(this.$element.find(`#${this.uniqueId}`)[0], { eventId: 'pay-frame-submit' });
    }

    setupListenerCallbacks() {
        this.payFrameLoadedCallback = () => {
            this.$timeout(() => {
                // Cancel the timer and let the view know the iframe was successfully loaded
                this.$timeout.cancel(this.iframeLoadTimeoutPromise);
                if (this.isLoading) {
                    this.isLoading = false;
                    this.isIframeLoadedSuccess = true;
                    // Reset error message to generic (in case it was set by the expired iframe)
                    this.errorMessage = this.settings.digitalPayError.creditCardEditCaptureFormLoadFailure;
                }
            });
        };

        this.payFrameErrorCallback = (response) => {
            this.$timeout(() => {
                // Error does not return cardId (it may do in the future) so will stop all pending updates
                this.isUpdating = false;

                if (response.code === this.settings.digitalPayErrorCodes.creditCardCaptureFormExpired) {
                    this.errorMessage = this.settings.digitalPayError.creditCardEditCaptureFormLoadFailure;
                    this.isIframeLoadedSuccess = false;
                }
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(this.settings.digitalPayError.creditCardUpdateFailMessage);
            });
        };

        this.payFrameSuccessCallback = (response) => {
            this.disposeEventListeners();
            if (this.isWallet) {
                this.closeEditMode();
            }
            this.onUpdate({ paymentInstrumentId: response.paymentInstrumentId, stepUpToken: response.stepUpToken });
            this.$timeout(() => {
                this.digitalPayPaymentErrorDisplayService.clearGlobalError();
            });
        };

        this.payFrameValidCallback = () => {
            this.$timeout(() => {
                this.isValid = true;
            });
            this.clearValidationState();
            this.fieldStructure = [true, true, true];
        };

        this.payFrameInvalidCallback = () => {
            this.$timeout(() => {
                this.isValid = false;
            });
        };

        this.payFrameFocusGainCallback = (response) => {
            this.currentSelectedField = this.setCurrentSelectedField(response);
            if (!this.focusLostObject) {
                return;
            }

            const errorMessage = this.digitalPayFormValidator.getUpdateSavedCreditCardError(this.focusLostObject, response.field);
            this.focusLostInvalidFieldsTimeoutPromise = this.callUpdateCardError(errorMessage);

            // Stop the missing fields message from displaying if focus on form is gained
            this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
            this.focusLostBlankFieldsTimeoutPromise = undefined;
        };

        this.payFrameFocusLostCallback = (response) => {
            this.currentSelectedField = this.creditCardEmptyField;
            if (response.allFieldsEmpty) {
                this.clearValidationState();
                return;
            }

            this.focusLostObject = response;

            // Used for validation when this.showValidationErrors is enabled
            this.fieldStructure = [
                this.focusLostObject.expiryMonthValid,
                this.focusLostObject.expiryYearValid,
                this.focusLostObject.cvvValid,
            ];

            const errorMessage = this.digitalPayFormValidator.getUpdateSavedCreditCardError(this.focusLostObject);
            this.focusLostInvalidFieldsTimeoutPromise = this.callUpdateCardError(errorMessage);

            // If the user moves away from the form, we display a missing fields message instead.
            // This is cancelled if the user focuses on the form immediately after moving away (e.g. tab from one field to another)
            if (this.hasBlankFields(this.focusLostObject)) {
                this.focusLostBlankFieldsTimeoutPromise = this.callUpdateCardError(this.settings.digitalPayError.creditCardMultipleValidationErrorsMessage);
            }
        };
    }

    registerEventListeners() {
        this.iframeEventHandler.on('pay-frame-loaded', this.payFrameLoadedCallback);
        this.iframeEventHandler.on('pay-frame-error', this.payFrameErrorCallback);
        this.iframeEventHandler.on('pay-frame-success', this.payFrameSuccessCallback);
        this.iframeEventHandler.on('pay-frame-valid', this.payFrameValidCallback);
        this.iframeEventHandler.on('pay-frame-invalid', this.payFrameInvalidCallback);
        this.iframeEventHandler.on('pay-frame-focus-gain', this.payFrameFocusGainCallback);
        this.iframeEventHandler.on('pay-frame-focus-lost', this.payFrameFocusLostCallback);
    }

    disposeEventListeners() {
        this.iframeEventHandler.off('pay-frame-loaded', this.payFrameLoadedCallback);
        this.iframeEventHandler.off('pay-frame-error', this.payFrameErrorCallback);
        this.iframeEventHandler.off('pay-frame-success', this.payFrameSuccessCallback);
        this.iframeEventHandler.off('pay-frame-valid', this.payFrameValidCallback);
        this.iframeEventHandler.off('pay-frame-invalid', this.payFrameInvalidCallback);
        this.iframeEventHandler.off('pay-frame-focus-gain', this.payFrameFocusGainCallback);
        this.iframeEventHandler.off('pay-frame-focus-lost', this.payFrameFocusLostCallback);
    }

    setupIframe() {
        this.isLoading = true;
        this.isIframeLoadedSuccess = false;
        this.iframeUrl = this.$sce.trustAsResourceUrl(!this.card.Expired ? this.card.UpdateURL : this.card.StepUp.Url);
        this.showIframe = true;

        // Setup a timer to wait for the iframe to load
        this.iframeLoadTimeoutPromise = this.$timeout(() => {
            this.isLoading = false;
        }, this.timeoutPeriodInMillseconds);
    }

    refreshIframe() {
        this.isLoading = true;
        this.onExpire();
        // Rely on $onChanges to get back the new stepup url
    }

    closeEditMode() {
        this.clearValidationState();
        this.onClose();
    }

    hasBlankFields(response) {
        return (response.cardValid === null || response.expiryMonthValid === null || response.expiryYearValid === null || response.cvvValid === null);
    }

    callUpdateCardError(errorMessage) {
        return this.$timeout(() => {
            this.onUpdateCardError({ errorMessage });
        }, this.settings.digitalPay.formFocusOutTimeoutMilliseconds);
    }

    clearValidationState() {
        this.focusLostObject = undefined;
        this.fieldStructure = [undefined, undefined, undefined];
        this.callUpdateCardError('');
        this.resetErrorDisplayTimeouts();
    }

    resetErrorDisplayTimeouts() {
        this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
        this.$timeout.cancel(this.focusLostInvalidFieldsTimeoutPromise);
        this.focusLostBlankFieldsTimeoutPromise = undefined;
        this.focusLostInvalidFieldsTimeoutPromise = undefined;
    }

    shouldShowValidation(fieldToCheck) {
        // Iframe response - True: Valid, False: Invalid, Null: Empty, Undefined: Hasn't been touched
        // Before submit, ignore empty fields
        return fieldToCheck === false;
    }

    shouldShowValidationSection() {
        return some(this.fieldStructure, (fieldValue, fieldIndex) => fieldValue === false && this.currentSelectedField !== fieldIndex);
    }

    setCurrentSelectedField(response) {
        switch (response.field) {
            case 'month':
                return this.creditCardFieldStructureMonth;
            case 'year':
                return this.creditCardFieldStructureYear;
            case 'cvv':
                return this.creditCardFieldStructureCvv;
            default:
                return this.creditCardEmptyField;
        }
    }

    $onChanges(changes) {
        if (changes.card && !changes.card.isFirstChange()) {
            this.setupIframe();
        }
    }
}

CreditCardItemEditController.$inject = ['$sce', '$timeout', '$element', 'Settings', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayIframeEventHandler', 'DigitalPayFormValidator'];

const creditCardItemEditComponentDefinition = {
    controller: CreditCardItemEditController,
    template: require('./credit-card-item-edit.component.html').trim(),
    bindings: {
        card: '<',
        updateText: '@?',
        showValidationErrors: '<?',
        onExpire: '&?',
        onClose: '&',
        onUpdate: '&?',
        isInline: '<?',
        isWallet: '<?',
        onUpdateCardError: '&',
    },
};

module.exports = {
    CreditCardItemEditController,
    creditCardItemEditComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/wallet/creditcard/credit-card-item-edit.component.js